import type { components } from '../../../../../unifiedapi';

type RiskAssessmentType = components['schemas']['RiskAssessmentType'];

/**
 * A supplier risk dimension, mirroring the scoring dimensions defined in Metis
 * (`app/core/scoring/constants.py`). Dimensions are either:
 *
 * - `computed` — alert events stamped with this dimension's type
 *   (`identity`, `network`, `underground`, `geopolitical`).
 * - `manual` — sourced from `CompanyRiskAssessment` records of a given type.
 */
interface BaseRiskDimension {
  /** Stable key used for the tab value / URL state and the event type filter. */
  key: string;
  /** Human-readable tab label. */
  label: string;
}

export interface ComputedRiskDimension extends BaseRiskDimension {
  kind: 'computed';
}

export interface ManualRiskDimension extends BaseRiskDimension {
  kind: 'manual';
  /** `CompanyRiskAssessment.type` backing this dimension. */
  assessmentType: RiskAssessmentType;
}

export type SupplierRiskDimension = ComputedRiskDimension | ManualRiskDimension;

/**
 * Type stamped on every supply-chain alert event. `types:supply-chain`
 * selects the whole set.
 */
export const SUPPLY_CHAIN_TYPE = 'supply-chain';

/** Metis filter for every supply-chain alert event. */
export const buildSupplyChainTypeFilter = () => `types:${SUPPLY_CHAIN_TYPE}`;

/**
 * Metis filter for one computed dimension. Each supply-chain event carries
 * exactly one of `identity`, `network`, `underground`, or `geopolitical`.
 */
export const buildDimensionTypeFilter = (dimensionKey: string) => `types:${dimensionKey}`;

/**
 * The six supplier risk dimensions, in Metis weight order. A computed
 * dimension's `key` is the event type Metis stamps on that dimension.
 */
export const SUPPLIER_RISK_DIMENSIONS: ReadonlyArray<SupplierRiskDimension> = [
  {
    key: 'identity',
    label: 'Identity',
    kind: 'computed',
  },
  {
    key: 'network',
    label: 'Network',
    kind: 'computed',
  },
  {
    key: 'underground',
    label: 'Underground',
    kind: 'computed',
  },
  {
    key: 'geopolitical',
    label: 'Geopolitical',
    kind: 'computed',
  },
  {
    key: 'internal-assessment',
    label: 'Internal Assessment',
    kind: 'manual',
    assessmentType: 'internal_assessment',
  },
  {
    key: 'external-assessment',
    label: 'External Assessment',
    kind: 'manual',
    assessmentType: 'external_assessment',
  },
];

/** Event-type tokens that should stay upper-cased when humanized. */
const EVENT_TYPE_ACRONYMS = new Set(['dns']);

/**
 * Turns a kebab-case STIX `EventType` (e.g. `dns-cache-poisoning`) into a
 * human-readable label (e.g. `DNS Cache Poisoning`).
 */
export const formatEventType = (type: string): string =>
  type
    .split('-')
    .map((word) =>
      EVENT_TYPE_ACRONYMS.has(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');

/** Metis filter limiting events to a single supplier (company). */
export const buildSupplierFilter = (supplierId: number) => `companies.id:(${supplierId})`;

/** Per-client event statuses selectable in the supplier findings filter. */
export const SUPPLIER_EVENT_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
] as const;

/** Assignee filter sentinel matching events with no assignee for the client. */
export const UNASSIGNED_VALUE = '__unassigned__';

/**
 * Metis filter matching the effective per-client status shown in the table. The
 * displayed status is resolved for the active client only (see
 * `getEffectiveClientStatus`), so the clauses are scoped by `client_id`:
 *
 * - `acknowledged` / `resolved` — an entry for the client with that status.
 * - `new` — no client entry carries an acknowledged/resolved status (covering
 *   both "no entry for this client" and an explicit `new`), mirroring the
 *   default applied in the UI.
 *
 * Returns `null` when nothing is selected (no clause to add).
 */
export const buildClientStatusFilter = (
  statuses: readonly string[],
  clientId: number | null
): string | null => {
  if (!statuses.length || clientId == null) return null;

  const clause = (status: string) =>
    status === 'new'
      ? `(NOT client_state:{client_id:${clientId} AND status:(acknowledged OR resolved)})`
      : `client_state:{client_id:${clientId} AND status:${status}}`;

  const clauses = statuses.map(clause);
  return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`;
};

/**
 * Metis filter matching the effective per-client assignee shown in the table.
 * Scoped by `client_id` to mirror `getEffectiveAssignee`. The
 * {@link UNASSIGNED_VALUE} sentinel matches events with no assignee for the
 * active client. Returns `null` when nothing is selected.
 */
export const buildAssigneeFilter = (
  assignees: readonly string[],
  clientId: number | null
): string | null => {
  if (!assignees.length || clientId == null) return null;

  const ids = assignees.filter((value) => value !== UNASSIGNED_VALUE);
  const includeUnassigned = assignees.includes(UNASSIGNED_VALUE);

  const clauses: string[] = [];
  if (ids.length) {
    clauses.push(`client_state:{client_id:${clientId} AND assignee.id:(${ids.join(' OR ')})}`);
  }
  if (includeUnassigned) {
    clauses.push(`(NOT client_state:{client_id:${clientId} AND assignee:*})`);
  }

  if (!clauses.length) return null;
  return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`;
};
